import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import supabase from '../utils/supabaseClient';

const Backdrop = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 40px;
  
  @media (max-width: 768px) {
    padding: 30px;
  }
  
  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const Modal = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 40px;
  max-width: 400px;
  width: 100%;
  color: #333;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  position: relative;
`;

const Title = styled.h2`
  font-size: 2rem;
  margin-bottom: 30px;
  text-align: center;
  background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Input = styled.input`
  padding: 15px;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #4ecdc4;
    transform: translateY(-2px);
    box-shadow: 0 2px 10px rgba(78, 205, 196, 0.2);
  }
`;

const AvatarSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
`;

const AvatarPreview = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #4ecdc4;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AvatarUpload = styled.div`
  width: 100%;
  position: relative;
  
  input[type="file"] {
    display: none;
  }
  
  label {
    display: block;
    padding: 10px;
    text-align: center;
    background: rgba(78, 205, 196, 0.1);
    border: 2px dashed #4ecdc4;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(78, 205, 196, 0.2);
    }
  }
`;

const Button = styled(motion.button)`
  padding: 15px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
  color: white;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  
  &:disabled {
    opacity: 0.5;
    cursor: wait;
  }
`;

const ErrorText = styled.p`
  color: #ff6b6b;
  font-size: 14px;
  margin-top: 5px;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  z-index: 10;
`;

const Spinner = styled.div`
  border: 4px solid rgba(78, 205, 196, 0.3);
  border-radius: 50%;
  border-top: 4px solid #4ecdc4;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-right: 10px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-weight: bold;
  color: #333;
`;

const JoinModal = ({ isOpen, onClose, onJoin, error: propError, isLoading, roomData }) => {
  const { t } = useLanguage();
  const [playerName, setPlayerName] = useState('');
  const [roomPassword, setRoomPassword] = useState('');
  const [avatarType, setAvatarType] = useState('default');
  const [joinError, setJoinError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const fileInputRef = useRef(null);

  // Reset the form when modal is opened
  useEffect(() => {
    if (isOpen) {
      setPlayerName('');
      setRoomPassword('');
      setAvatarType('default');
      setJoinError('');
      setIsSubmitting(false);
      setAvatarFile(null);
      setAvatarPreview(null);
    }
  }, [isOpen]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Create a preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result);
      setAvatarType('custom');
    };
    reader.readAsDataURL(file);
    setAvatarFile(file);
  };

  const handleClickAvatar = () => {
    fileInputRef.current.click();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (isSubmitting || isLoading) return;
    
    // Basic validation
    if (!playerName.trim()) {
      setJoinError(t('nameRequired'));
      return;
    }

    // Check if private room requires password
    if (roomData?.is_private && !roomPassword.trim()) {
      setJoinError(t('passwordRequired'));
      return;
    }
    
    try {
      setIsSubmitting(true);
      setJoinError('');
      
      // Use avatar preview if we have a custom avatar, otherwise use null for default
      const avatarToUse = avatarType === 'custom' ? avatarPreview : null;
      console.log('Attempting to join with:', { 
        playerName: playerName.trim(), 
        avatar: avatarToUse,
        password: roomData?.is_private ? roomPassword : null
      });
      
      const success = await onJoin(playerName.trim(), avatarToUse, roomData?.is_private ? roomPassword : null);
      
      if (!success) {
        setJoinError(t('failedToJoin'));
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Error in join modal submit:', error);
      if (error.message === 'Incorrect password') {
        setJoinError(t('incorrectPassword'));
      } else {
      setJoinError(error.message || 'An error occurred');
      }
      setIsSubmitting(false);
    }
  };

  // If modal is closed, don't render anything
  if (!isOpen) return null;

  // Display any error from props or local state
  const displayError = joinError || propError;

  return (
    <Backdrop
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <Modal
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ type: "spring", duration: 0.5 }}
      >
        {(isLoading || isSubmitting) && (
          <LoadingOverlay>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Spinner />
              <LoadingText>{t('joining')}</LoadingText>
            </div>
          </LoadingOverlay>
        )}
        
        <Title>{t('joinTheGame')}</Title>
        
        <Form onSubmit={handleSubmit}>
          <Input
            type="text"
            placeholder={t('enterYourName')}
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            disabled={isSubmitting || isLoading}
            required
            autoFocus
            maxLength={15}
          />
          
          {roomData?.is_private && (
            <Input
              type="password"
              placeholder={t('enterPassword')}
              value={roomPassword}
              onChange={(e) => setRoomPassword(e.target.value)}
              disabled={isSubmitting || isLoading}
              required
            />
          )}
          
          <AvatarSection>
            <AvatarPreview>
              {avatarPreview ? (
                <img src={avatarPreview} alt="Avatar preview" />
              ) : (
                <img src="/default-avatar.svg" alt="Default avatar" />
              )}
            </AvatarPreview>
            
            <AvatarUpload>
              <label htmlFor="avatar-upload">
                {t('chooseProfilePicture')}
              </label>
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleFileSelect}
                disabled={isSubmitting || isLoading}
              />
            </AvatarUpload>
          </AvatarSection>
          
          {displayError && <ErrorText>{displayError}</ErrorText>}
          
          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            {onClose && (
              <Button 
                type="button" 
                onClick={onClose}
                disabled={isSubmitting || isLoading}
                style={{ 
                  background: 'rgba(0,0,0,0.1)', 
                  flex: '1' 
                }}
              >
                {t('cancel')}
              </Button>
            )}
            
            <Button
              type="submit"
              disabled={isSubmitting || isLoading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{ flex: '2' }}
            >
              {isSubmitting || isLoading ? t('joining') : t('joinGame')}
            </Button>
          </div>
        </Form>
      </Modal>
    </Backdrop>
  );
};

export default JoinModal; 